import React, { useContext, useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import { cityList, initSideModal, usersRoles } from 'helpers/utils';
import MultiSelect from 'components/common/multi-select';
import Select, { IDefaultOption } from 'components/common/select';
import InputField from 'components/common/input-field';
import DatePicker from 'components/common/date-picker';
import { setSideModalData } from 'store/features/modelSlice';
import { IClientUser, addUser, editUserAPI, getVehiclesList } from 'store/features/userSlice';
import { emailRegEx, onlyNumbers, countryList, stateList, paymentsOptions } from 'helpers/utils';
import PhoneInputComponent from 'components/common/phone-input';
import moment from 'moment';
import toast from 'react-hot-toast';
import { AppContext } from 'context/app-context';
import CommonLayout from './common-layout';
import { getCompanyList } from 'store/features/companySlice';
import { getOrganizationListWithAssociateParking } from 'store/features/administratorRoleSlice';
import {
  IClientAssignedVehicleOptionItem,
  IClientUserAddEditData,
} from 'interfaces/users-interface';
import { useTranslation } from 'react-i18next';

export interface IUserError
  extends Pick<IClientUser, 'email' | 'mobile_no' | 'name' | 'surname' | 'license_no'> {
  organization?: string;
}
export interface IAddClientUserRequest extends Omit<IClientUser, 'date_of_birth'> {
  birth_date: string;
  role_details?: { role: string; organization_id: string }[];
  vehicle_ids: string[];
  company_id?: string;
}
export type IEditClientUserRequest = Omit<
  IAddClientUserRequest,
  | 'dni_no'
  | 'dni_exp_date'
  | 'license_no'
  | 'license_exp_date'
  | 'policy_no'
  | 'policy_exp_date'
  | 'organization_id'
  | 'role'
>;

const userInitData: IClientUserAddEditData = {
  name: '',
  surname: '',
  email: '',
  country_code: '',
  mobile_no: '',
  date_of_birth: '',
  address: null,
  dni_no: '',
  dni_exp_date: '',
  license_no: '',
  license_exp_date: '',
  billing_address: null,
  policy_no: '',
  policy_exp_date: '',
  assigned_vehicles: [],
  associated_company: null,
  purchase_type: null,
  organization_id: '',
};

const AddUser: React.FC = () => {
  const dispatch = useAppDispatch();
  const { setLoader } = useContext(AppContext);
  const { t } = useTranslation();

  const { selectedOrganization } = useAppSelector(state => state.auth);
  const { editUser, vehicles, isAddUserModalOpenFromUserModule } = useAppSelector(
    state => state.user,
  );
  const { filledVehicleData } = useAppSelector(state => state.vehicles);
  const { organizationAssociateParking } = useAppSelector(state => state.adminRole);
  const { sideModal } = useAppSelector(state => state.modal);
  const { companies } = useAppSelector(state => state.company);

  const [user, setUser] = useState<IClientUserAddEditData>(userInitData);
  const [dropdownSearch, setDropdownSearch] = useState<string>('');
  const [error, setError] = useState<Partial<IUserError>>({});
  const [vehicleOptions, setVehicleOptions] = useState<IDefaultOption[]>([]);
  const [companyOptions, setCompanyOptions] = useState<IDefaultOption[]>([]);

  let mob_num_length_ref = useRef(0);
  const { content } = sideModal;
  const { formFor, callBackFun } = content;
  const isSuperAdmin = selectedOrganization?.role_name === usersRoles?.SUPER_ADMIN;
  const isFleetManager = selectedOrganization?.role_name === usersRoles?.FLEET_MANAGE;
  const isDealerAdmin = selectedOrganization?.role_name === usersRoles?.DEALER_ADMIN;

  const getErrorData = () => {
    return {
      email: !user?.email?.trim()
        ? t('users.enterEmail')
        : !emailRegEx.test(user?.email)
        ? t('users.enterValidEmail')
        : '',
      mobile_no:
        !user?.mobile_no?.trim() || !user?.country_code?.trim() ? t('users.enterPhone') : '',
      organization: !user.organization_id?.trim() ? t('PLEASE_SELECT_ORG') : '',
      name: !user.name?.trim() ? t('users.enterName') : '',
      surname: !user.surname?.trim() ? t('users.enterSurname') : '',
      license_no: !user.license_no?.trim() ? 'Please enter licence number' : '',
    };
  };
  const isValid = () => {
    if (
      (isSuperAdmin && !user.organization_id.trim()) ||
      !user?.email?.trim() ||
      !emailRegEx.test(user?.email) ||
      !user?.mobile_no?.trim() ||
      !user?.country_code?.trim() ||
      !user?.name?.trim() ||
      !user?.surname?.trim() ||
      !user?.license_no?.trim()
    ) {
      const output = getErrorData();
      setError(output);
      return false;
    } else {
      const output = getErrorData();
      setError(output);
      return true;
    }
  };
  const handleChange = (field: string, value: string | IDefaultOption, subField: string = '') => {
    let updatedData = { ...user };
    switch (field) {
      case 'address':
      case 'billing_address': {
        updatedData = { ...updatedData, [field]: { ...updatedData[field], [subField]: value } };
        break;
      }
      default: {
        updatedData = { ...updatedData, [field]: value };
        break;
      }
    }
    setUser(field === 'organization_id' ? { ...updatedData, assigned_vehicles: [] } : updatedData);
  };
  const handleMobileNumber = (data: { code: string; number: string; format: string }) => {
    setUser({ ...user, mobile_no: data.number, country_code: data.code });
    mob_num_length_ref.current =
      data.format.replaceAll('+', '').replaceAll(' ', '').replaceAll('-', '').length -
      data.code.length;
  };
  const handleAddUser = () => {
    setLoader(true);
    let request: IAddClientUserRequest = {
      name: user?.name,
      surname: user?.surname,
      email: user?.email,
      country_code: user?.country_code,
      mobile_no: user?.mobile_no,
      birth_date: user?.date_of_birth ? moment(user?.date_of_birth).format('DD/MM/YYYY') : '',
      address:
        (user?.address &&
          Object.values(user?.address).filter(field => String(field)?.trim()).length === 0) ||
        !user?.address
          ? null
          : {
              address1: user?.address?.address1 || '',
              address2: user?.address?.address2 || '',
              city: user?.address?.city || '',
              state: user?.address?.state || '',
              country: user?.address?.country || '',
              zipcode: user?.address?.zipcode || '',
            },
      dni_no: user.dni_no,
      dni_exp_date: user?.dni_exp_date ? moment(user?.dni_exp_date).format('DD/MM/YYYY') : '',
      license_no: user?.license_no,
      license_exp_date: user?.license_exp_date
        ? moment(user?.license_exp_date).format('DD/MM/YYYY')
        : '',
      billing_address: user.billing_address,
      policy_no: user.policy_no,
      policy_exp_date: user.policy_exp_date
        ? moment(user?.policy_exp_date).format('DD/MM/YYYY')
        : '',
      type_of_purchase_ids: [],
      vehicle_ids: user?.assigned_vehicles?.map(data => data?.id || '') || [],
      role_details: [
        {
          role: usersRoles?.CLIENT,
          organization_id: isSuperAdmin
            ? user?.organization_id || ''
            : selectedOrganization?.organization_id || '',
        },
      ],
    };
    if (user?.associated_company) {
      request = { ...request, company_id: user?.associated_company.id || '' };
    }
    if (!request?.address?.address2?.trim()) {
      delete request?.address?.address2;
    }
    if (!request?.billing_address?.address2?.trim()) {
      delete request?.billing_address?.address2;
    }

    dispatch(addUser({ request }))
      .then(() => {
        callBackFun && callBackFun();
        if (isAddUserModalOpenFromUserModule) {
          toast.success('User added successfully');
          dispatch(setSideModalData(initSideModal));
        }
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const handleEditUser = () => {
    setLoader(true);
    let request: IEditClientUserRequest = {
      name: user?.name,
      surname: user?.surname,
      email: user?.email,
      country_code: user?.country_code,
      mobile_no: user?.mobile_no,
      birth_date: user?.date_of_birth ? moment(user?.date_of_birth).format('DD/MM/YYYY') : '',
      address:
        (user?.address &&
          Object.values(user?.address).filter(field => String(field)?.trim()).length === 0) ||
        !user?.address
          ? null
          : {
              address1: user?.address?.address1 || '',
              address2: user?.address?.address2 || '',
              city: user?.address?.city || '',
              state: user?.address?.state || '',
              country: user?.address?.country || '',
              zipcode: user?.address?.zipcode || '',
            },
      billing_address:
        (user?.billing_address &&
          Object.values(user?.billing_address).filter(field => String(field)?.trim()).length ===
            0) ||
        !user?.billing_address
          ? null
          : {
              address1: user?.billing_address?.address1 || '',
              address2: user?.billing_address?.address2 || '',
              city: user?.billing_address?.city || '',
              state: user?.billing_address?.state || '',
              country: user?.billing_address?.country || '',
              zipcode: user?.billing_address?.zipcode || '',
            },
      type_of_purchase_ids: [],
      vehicle_ids: user?.assigned_vehicles?.map(data => data?.id || '') || [],
      role_details: [
        {
          role: usersRoles?.CLIENT,
          organization_id: isSuperAdmin
            ? user.organization_id || ''
            : selectedOrganization?.organization_id || '',
        },
      ],
    };
    if (user?.associated_company) {
      request = { ...request, company_id: user?.associated_company.id || '' };
    }
    if (isFleetManager && request.role_details?.length) {
      delete request?.role_details;
    }
    if (!request?.address?.address2?.trim()) {
      delete request?.address?.address2;
    }
    if (!request?.billing_address?.address2?.trim()) {
      delete request?.billing_address?.address2;
    }

    dispatch(editUserAPI({ request, userId: user?._id || '' }))
      .then(() => {
        callBackFun && callBackFun();
        if (isAddUserModalOpenFromUserModule) {
          toast.success('User updated successfully');
          dispatch(setSideModalData(initSideModal));
        }
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const handleSubmit = () => {
    if (!isValid()) {
      return;
    }
    if (
      user?.address &&
      Object.entries(user?.address).filter(([key, val]) => key !== 'address2' && String(val).trim())
        .length > 0 &&
      Object.entries(user?.address).filter(([key, val]) => key !== 'address2' && String(val).trim())
        .length < 5
    ) {
      toast.error(t('users.fillAddressData'));
      return;
    }
    if (
      user?.billing_address &&
      Object.entries(user?.billing_address).filter(
        ([key, val]) => key !== 'address2' && String(val).trim(),
      ).length > 0 &&
      Object.entries(user?.billing_address).filter(
        ([key, val]) => key !== 'address2' && String(val).trim(),
      ).length < 5
    ) {
      toast.error(t('users.fillBillingAddressData'));

      return;
    }
    // if (user?.assigned_vehicles?.length === 0 && !user.associated_company) {
    //   toast.error('Please select associate company or assigned vehicles');
    //   return;
    // }
    formFor === 'EDIT' ? handleEditUser() : handleAddUser();
  };
  const getStateList = (key: 'address' | 'billing_address') => {
    const selectedCountry = countryList.find(
      (data: { name: string }) => data.name === user[key]?.country,
    );
    return stateList(selectedCountry?.isoCode) || [];
  };
  const getSelectedState = (key: 'address' | 'billing_address') => {
    const selectedCountry = countryList.find(
      (data: { name: string }) => data.name === user[key]?.country,
    );
    return (
      stateList(selectedCountry?.isoCode).find(
        (data: { name: string }) => data.name === user[key]?.state,
      ) || null
    );
  };
  const getCityList = (key: 'address' | 'billing_address') => {
    const selectedState = getSelectedState(key);
    return cityList(selectedState?.countryCode, selectedState?.isoCode) || [];
  };
  const getSelectedCity = (key: 'address' | 'billing_address') => {
    const selectedState = getSelectedState(key);
    return (
      cityList(selectedState?.countryCode, selectedState?.isoCode).find(
        (data: { name: string }) => data.name === user[key]?.city,
      ) || null
    );
  };
  const fetchVehiclesList = async () => {
    try {
      let params = {};
      if (isSuperAdmin && !user.organization_id) {
        return;
      }
      if (isSuperAdmin && user.organization_id) {
        params = { organization_id: user?.organization_id || '' };
      } else if (isDealerAdmin) {
        params = { organization_id: selectedOrganization?.organization_id };
      }
      const response = await dispatch(getVehiclesList({ params: { ...params } }))
        .then(() => setLoader(false))
        .catch(() => setLoader(false));
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  };
  const getAllOptions = async () => {
    try {
      setLoader(true);
      const promises = [fetchVehiclesList(), dispatch(getCompanyList({ params: {}, url: '' }))];
      if (isSuperAdmin) {
        promises.push(dispatch(getOrganizationListWithAssociateParking({ params: {} })));
      }
      const res = await Promise.all(promises).catch(err => {
        return Promise.reject(err);
      });
      return Promise.resolve(res);
    } catch (error) {
      return Promise.reject(error);
    }
  };
  const fetchData = async () => {
    await getAllOptions();
  };
  useEffect(() => {
    try {
      fetchData();
      if (!isAddUserModalOpenFromUserModule) {
        setUser({
          ...user,
          organization_id:
            filledVehicleData?.organization?.id || filledVehicleData?.organization?._id || '',
        });
      }
    } catch (error) {
      setLoader(false);
    }
  }, []);
  useEffect(() => {
    try {
      if (editUser && formFor === 'EDIT') {
        setLoader(true);
        let formatedUserData: IClientUserAddEditData = {
          address: editUser?.address?.address1 ? editUser?.address : null,
          billing_address: editUser?.billing_address?.address1 ? editUser?.billing_address : null,
          assigned_vehicles:
            [...editUser?.vehicle_details].map(data => ({
              id: data?._id,
              name: `${data?.model} (${data?.plate})`,
              value: data?.plate,
            })) || [],
          associated_company:
            editUser?.company_details && editUser?.company_details.length
              ? { id: editUser?.company_details[0]?._id, name: editUser?.company_details[0]?.name }
              : null,
          country_code: editUser?.country_code,
          date_of_birth: editUser?.date_of_birth,
          dni_exp_date: editUser?.dni_exp_date,
          dni_no: editUser?.dni_no,
          email: editUser?.email,
          license_exp_date: editUser?.license_exp_date,
          license_no: editUser?.license_no,
          mobile_no: editUser?.mobile_no,
          name: editUser?.name,
          policy_exp_date: editUser?.policy_exp_date,
          policy_no: editUser?.policy_no,
          purchase_type: null,
          surname: editUser?.surname,
          _id: editUser?.id,
          organization_id: editUser?.organization_id ? editUser?.organization_id : '',
        };
        setUser(formatedUserData);
        setLoader(false);
      }
    } catch (error) {
      setLoader(false);
    }
  }, []);
  const setVehicleOption = () => {
    let updatedVehicleOptions: IClientAssignedVehicleOptionItem[] = [];
    [...vehicles].forEach(data => {
      if ((user?.assigned_vehicles || [])?.filter(item => item?.id === data?._id).length === 0) {
        updatedVehicleOptions = [...updatedVehicleOptions, data];
      }
    });
    (editUser?.vehicle_details || [])?.forEach(data => {
      if (
        updatedVehicleOptions.filter(item => item._id === data._id).length === 0 &&
        (user?.assigned_vehicles || []).filter(item => item.id === data._id).length === 0 &&
        data?.organization_id === (user.organization_id || selectedOrganization?.organization_id)
      ) {
        updatedVehicleOptions = [
          ...updatedVehicleOptions,
          {
            _id: data?._id || '',
            model: `${data?.model_data?.name}` || '',
            plate: data?.plate || '',
            vehicle_data: {
              parking_id: data?.extendedVehicleDetails.parking_id || '',
            },
          },
        ];
      }
    });
    const formatedVehicleList = updatedVehicleOptions.map(data => ({
      id: data?._id,
      name: `${data?.model} (${data?.plate})`,
      value: data?.plate,
    }));
    setVehicleOptions(formatedVehicleList);
  };
  useEffect(() => {
    setVehicleOption();
  }, [user?.assigned_vehicles, vehicles]);
  useEffect(() => {
    if (isSuperAdmin) {
      setLoader(true);
      fetchVehiclesList();
      setLoader(false);
    }
  }, [user.organization_id]);
  useEffect(() => {
    try {
      const formatedOptions = companies.map(data => ({
        id: data?._id || '',
        name: data.name || '',
      }));
      setCompanyOptions(formatedOptions);
    } catch (error) {
      setLoader(false);
    }
  }, [companies]);
  const handleCancel = () => {
    try {
      if (!isAddUserModalOpenFromUserModule && callBackFun) {
        callBackFun();
      } else {
        dispatch(setSideModalData(initSideModal));
      }
    } catch (error) {}
  };

  return (
    <CommonLayout
      onConfirm={() => handleSubmit()}
      confirmButtonProps={{ label: formFor === 'EDIT' ? t('SAVE_CHANGES') : t('users.addUser') }}
      onCancel={() => handleCancel()}
    >
      {/*Admin Role Inputs*/}
      <div className="flex flex-wrap p-6">
        <div className="flex flex-wrap -mx-3 w-full">
          {isSuperAdmin && (
            <div className="w-1/2 px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black w-full">
                  {`${t('users.organization')}`}
                  <span className="text-red-500 ml-[2px] text-base font-bold">*</span>
                </label>
                <Select
                  options={organizationAssociateParking.filter(
                    org =>
                      org?.organization_name
                        ?.toLowerCase()
                        .startsWith(dropdownSearch.toLowerCase()),
                  )}
                  selected={
                    organizationAssociateParking?.find(
                      data => data?._id === user?.organization_id,
                    ) || null
                  }
                  setSelected={async val =>
                    setUser({ ...user, organization_id: val._id || val?.id })
                  }
                  placeholder={t('SELE_ORG')}
                  error={error?.organization || ''}
                  isSearchable
                  search={dropdownSearch}
                  removeSearchOnBlur={true}
                  disabled={!isAddUserModalOpenFromUserModule || isFleetManager ? true : false}
                />
              </div>
            </div>
          )}
          <div className="w-1/2 px-3 max-w-1/2">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {t('users.assignedVehicles')}
              </label>
              <MultiSelect
                options={vehicleOptions.filter(
                  veh =>
                    veh?.value && veh?.value.toLowerCase().includes(dropdownSearch.toLowerCase()),
                )}
                selected={user?.assigned_vehicles || []}
                placeholder={t('users.selectVehicles')}
                setSelected={vehicles => setUser({ ...user, assigned_vehicles: [...vehicles] })}
                isSearchable
                search={dropdownSearch}
                setSearch={data => setDropdownSearch(data)}
                removeSearchOnBlur={true}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {`${t('users.name')}`}
                <span className="text-red-500 ml-1 text-base font-bold">*</span>
              </label>
              <InputField
                placeholder={t('users.name')}
                value={user?.name}
                onChange={val => setUser({ ...user, name: val })}
                disabled={isFleetManager}
                error={error?.name}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {`${t('users.surname')}`}
                <span className="text-red-500 ml-1 text-base font-bold">*</span>
              </label>
              <InputField
                placeholder={t('users.surname')}
                value={user?.surname}
                onChange={val => setUser({ ...user, surname: val })}
                disabled={isFleetManager}
                error={error?.surname}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {`${t('users.email')}`}
                <span className="text-red-500 ml-1 text-base font-bold">*</span>
              </label>
              <InputField
                placeholder={t('users.email')}
                value={user?.email}
                onChange={val => setUser({ ...user, email: val })}
                error={error?.email || ''}
                disabled={isFleetManager || formFor === 'EDIT'}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {`${t('users.mobileNo')}`}
                <span className="text-red-500 ml-1 text-base font-bold">*</span>
              </label>
              <PhoneInputComponent
                value={user?.mobile_no}
                countryCode={user?.country_code}
                onChange={data => handleMobileNumber(data)}
                error={error?.mobile_no}
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {t('users.dateOfBirth')}
              </label>
              <DatePicker
                selected={user?.date_of_birth || ''}
                onChange={date => setUser({ ...user, date_of_birth: String(date) })}
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {t('users.associateCompany')}
              </label>
              <Select
                options={companyOptions}
                selected={user?.associated_company || null}
                placeholder={t('PLEASE_SEL_ASS_COM')}
                setSelected={data => setUser({ ...user, associated_company: data })}
                showCross
                handleCross={() => setUser({ ...user, associated_company: null })}
                disabled={isFleetManager}
              />
            </div>
          </div>
          {isSuperAdmin ? <div className="w-1/2 px-3"></div> : <></>}
          <div className="w-1/2 px-3">
            <div className="flex flex-wrap -mx-3">
              <div className="w-full px-3 mt-1">
                <label className="font-bold text-base text-black2">{t('users.DNIDetails')}</label>
              </div>
              <div className="w-1/2 px-3">
                <div className="mb-3">
                  <label className="font-medium text-sm text-black w-full">
                    {t('users.dniNo')}
                  </label>
                  <InputField
                    placeholder={t('users.eightDigitNo')}
                    value={user.dni_no}
                    onChange={val => setUser({ ...user, dni_no: val })}
                    disabled={formFor === 'EDIT'}
                  />
                </div>
              </div>
              <div className="w-1/2 px-3">
                <div className="mb-3">
                  <label className="font-medium text-sm text-black w-full">
                    {t('users.expDate')}
                  </label>
                  <DatePicker
                    placement="BOTTOM_RIGHT"
                    selected={user?.dni_exp_date || ''}
                    onChange={date => setUser({ ...user, dni_exp_date: String(date) })}
                    disabled={formFor === 'EDIT'}
                    isAllowPastDate={false}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="flex flex-wrap -mx-3">
              <div className="w-full px-3 mt-1">
                <label className="font-bold text-base text-black2">
                  {t('users.drivingLicenceDetails')}
                </label>
              </div>
              <div className="w-1/2 px-3">
                <div className="mb-3">
                  <label className="font-medium text-sm text-black w-full">
                    {`${t('users.licenseNo')}`}
                    <span className="text-red-500 ml-1 text-base font-bold">*</span>
                  </label>
                  <InputField
                    placeholder={t('users.enterLicenceNo')}
                    value={user.license_no}
                    onChange={val => handleChange('license_no', val)}
                    error={error?.license_no || ''}
                  />
                </div>
              </div>
              <div className="w-1/2 px-3">
                <div className="mb-3">
                  <label className="font-medium text-sm text-black w-full">
                    {t('users.expDate')}
                  </label>
                  <DatePicker
                    placement="BOTTOM_RIGHT"
                    selected={user?.license_exp_date || ''}
                    onChange={date => setUser({ ...user, license_exp_date: String(date) })}
                    isAllowPastDate={false}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="w-full px-3">
            <label className="font-bold text-base text-black2">{t('users.insuranceDetails')}</label>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">{t('users.policyNo')}</label>
              <InputField
                placeholder={t('users.policyNo')}
                value={user.policy_no}
                onChange={val => setUser({ ...user, policy_no: val })}
                disabled={formFor === 'EDIT'}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">{t('users.expDate')}</label>
              <div className="flex w-full relative">
                <DatePicker
                  placement="BOTTOM_RIGHT"
                  selected={user?.policy_exp_date || ''}
                  onChange={date => setUser({ ...user, policy_exp_date: String(date) })}
                  disabled={formFor === 'EDIT'}
                  isAllowPastDate={false}
                />
              </div>
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {t('users.typeOfPurchase')}
              </label>
              <Select<IDefaultOption>
                placeholder={t('users.selectPurchaseType')}
                options={paymentsOptions || []}
                selected={user?.purchase_type || null}
                setSelected={paymentOpt => setUser({ ...user, purchase_type: paymentOpt })}
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-full px-3">
            <label className="font-medium text-sm text-black w-full">{t('users.address')}</label>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-2">
              <InputField
                placeholder={t('ADDRESS1')}
                value={user?.address?.address1 || ''}
                onChange={val => handleChange('address', val, 'address1')}
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-2">
              <InputField
                placeholder={t('ADDRESS2')}
                value={user?.address?.address2 || ''}
                onChange={val => handleChange('address', val, 'address2')}
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <Select
                placeholder={t('users.country')}
                selected={
                  countryList.find(
                    (data: { name: string }) => data.name === user.address?.country,
                  ) || { id: '00', name: user.address?.country || '' }
                }
                setSelected={data => handleChange('address', data?.name || '', 'country')}
                options={countryList.filter(
                  coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                )}
                isSearchable
                search={dropdownSearch}
                setSearch={val => {
                  setDropdownSearch(val);
                  handleChange('address', val, 'country');
                }}
                dropdownPlacement="TOP"
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <Select
                placeholder={t('users.state')}
                selected={
                  getSelectedState('address') || { id: '00', name: user.address?.state || '' }
                }
                setSelected={data => handleChange('address', data?.name || '', 'state')}
                options={getStateList('address').filter(
                  state => state?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                )}
                isSearchable
                search={dropdownSearch}
                setSearch={val => {
                  setDropdownSearch(val);
                  handleChange('address', val, 'state');
                }}
                dropdownPlacement="TOP"
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <Select
                placeholder={t('users.city')}
                selected={
                  getSelectedCity('address') || { id: '00', name: user.address?.city || '' }
                }
                setSelected={data => handleChange('address', data?.name || '', 'city')}
                options={getCityList('address').filter(
                  cit => cit?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                )}
                isSearchable
                search={dropdownSearch}
                setSearch={val => {
                  setDropdownSearch(val);
                  handleChange('address', val, 'city');
                }}
                dropdownPlacement="TOP"
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <InputField
                placeholder={t('users.postalCode')}
                value={user.address?.zipcode || ''}
                onChange={val => {
                  if (val && !onlyNumbers.test(val)) {
                    return;
                  }
                  handleChange('address', val, 'zipcode');
                }}
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-full px-3">
            <label className="font-medium text-sm text-black w-full">
              {t('users.billingAddress')}
            </label>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-2">
              <InputField
                placeholder={t('ADDRESS1')}
                value={user?.billing_address?.address1 || ''}
                onChange={val => handleChange('billing_address', val, 'address1')}
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-2">
              <InputField
                placeholder={t('ADDRESS2')}
                value={user?.billing_address?.address2 || ''}
                onChange={val => handleChange('billing_address', val, 'address2')}
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <Select
                placeholder={t('users.country')}
                selected={
                  countryList.find(
                    (data: { name: string }) => data.name === user.billing_address?.country,
                  ) || { id: '00', name: user.billing_address?.country || '' }
                }
                setSelected={data => handleChange('billing_address', data?.name || '', 'country')}
                options={countryList.filter(
                  coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                )}
                isSearchable
                search={dropdownSearch}
                setSearch={val => {
                  setDropdownSearch(val);
                  handleChange('billing_address', val, 'country');
                }}
                dropdownPlacement="TOP"
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <Select
                placeholder={t('users.state')}
                selected={
                  getSelectedState('billing_address') || {
                    id: '00',
                    name: user.billing_address?.state || '',
                  }
                }
                setSelected={data => handleChange('billing_address', data?.name || '', 'state')}
                options={getStateList('billing_address').filter(
                  state => state?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                )}
                isSearchable
                search={dropdownSearch}
                setSearch={val => {
                  setDropdownSearch(val);
                  handleChange('billing_address', val, 'state');
                }}
                dropdownPlacement="TOP"
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <Select
                placeholder={t('users.state')}
                selected={
                  getSelectedCity('billing_address') || {
                    id: '00',
                    name: user.billing_address?.city || '',
                  }
                }
                setSelected={data => handleChange('billing_address', data?.name || '', 'city')}
                options={getCityList('billing_address').filter(
                  cit => cit?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                )}
                isSearchable
                search={dropdownSearch}
                setSearch={val => {
                  setDropdownSearch(val);
                  handleChange('billing_address', val, 'city');
                }}
                dropdownPlacement="TOP"
                disabled={isFleetManager}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <InputField
                placeholder={t('users.postalCode')}
                value={user.billing_address?.zipcode || ''}
                onChange={val => {
                  if (val && !onlyNumbers.test(val)) {
                    return;
                  }
                  handleChange('billing_address', val, 'zipcode');
                }}
                disabled={isFleetManager}
              />
            </div>
          </div>
        </div>
      </div>
    </CommonLayout>
  );
};

export default AddUser;
